import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { getAdminResources, createResource, updateResource, deleteResource, getVerticals } from "@/lib/api";
import { Plus, Trash2, Loader2, FileText, Video, BookOpen, FileDown, Edit, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const RESOURCE_TYPES = [
  { value: "white_paper", label: "White Paper", icon: FileText },
  { value: "ebook", label: "Presentation", icon: BookOpen },
  { value: "product_video", label: "Product Video", icon: Video },
  { value: "case_study_video", label: "Case Study Video", icon: Video },
  { value: "case_study_doc", label: "Case Study Doc", icon: FileText },
  { value: "brochure", label: "Brochure", icon: FileDown },
];

const TYPE_LABEL = Object.fromEntries(RESOURCE_TYPES.map(t => [t.value, t.label]));

const emptyForm = { title: "", resource_type: "white_paper", description: "", file_url: "", thumbnail_url: "", vertical_id: "", is_published: true };

export default function AdminResources() {
  const [resources, setResources] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [vertFilter, setVertFilter] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [resRes, vertRes] = await Promise.all([getAdminResources(), getVerticals()]);
        setResources(resRes.data);
        setVerticals(vertRes.data);
      } catch { toast.error("Failed to load resources"); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const openCreate = () => { setForm({ ...emptyForm }); setEditId(null); setShowDialog(true); };
  const openEdit = (r) => {
    setForm({ title: r.title, resource_type: r.resource_type, description: r.description, file_url: r.file_url, thumbnail_url: r.thumbnail_url || "", vertical_id: r.vertical_id || "", is_published: r.is_published });
    setEditId(r.id);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.resource_type) { toast.error("Title and type are required"); return; }
    setSaving(true);
    try {
      if (editId) {
        await updateResource(editId, form);
        setResources(prev => prev.map(r => r.id === editId ? { ...r, ...form } : r));
        toast.success("Resource updated!");
      } else {
        const res = await createResource(form);
        setResources(prev => [res.data, ...prev]);
        toast.success("Resource created!");
      }
      setShowDialog(false);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await deleteResource(id);
      setResources(prev => prev.filter(r => r.id !== id));
      toast.success("Deleted!");
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = resources.filter(r => {
    if (filter && r.resource_type !== filter) return false;
    if (vertFilter && r.vertical_id !== vertFilter) return false;
    return true;
  });

  return (
    <AdminLayout title="Resources" subtitle="Manage white papers, e-books, videos, case studies and brochures">
      <div className="flex flex-wrap gap-3 mb-6 items-center justify-between">
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant={!filter ? "default" : "outline"} size="sm" onClick={() => setFilter("")}>All ({resources.length})</Button>
          {RESOURCE_TYPES.map(t => {
            const count = resources.filter(r => r.resource_type === t.value).length;
            return <Button key={t.value} variant={filter === t.value ? "default" : "outline"} size="sm" onClick={() => setFilter(t.value)}>{t.label} ({count})</Button>;
          })}
          <div className="h-6 w-px bg-slate-200 mx-1" />
          <Select value={vertFilter || "all"} onValueChange={(v) => setVertFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="resource-vertical-filter">
              <SelectValue placeholder="All Verticals" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Verticals</SelectItem>
              {verticals.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openCreate} className="bg-[#0066CC] hover:bg-[#0055aa]" data-testid="add-resource-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Resource
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-500">No resources yet. Click "Add Resource" to get started.</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Vertical</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(r => (
              <TableRow key={r.id}>
                <TableCell>
                  <div className="text-sm font-medium max-w-xs truncate">{r.title}</div>
                  {r.description && <div className="text-xs text-slate-400 truncate max-w-xs">{r.description}</div>}
                </TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{TYPE_LABEL[r.resource_type] || r.resource_type}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{verticals.find(v => v.id === r.vertical_id)?.name || "-"}</TableCell>
                <TableCell>
                  {r.is_published ? (
                    <span className="flex items-center gap-1 text-xs text-green-600"><Eye className="w-3 h-3" />Published</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-slate-400"><EyeOff className="w-3 h-3" />Draft</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "-"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-resource-${r.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Resource" : "Add Resource"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Resource title" data-testid="resource-title-input" className="placeholder:text-slate-300" />
            </div>
            <div className="space-y-1.5">
              <Label>Type *</Label>
              <Select value={form.resource_type} onValueChange={(val) => setForm({...form, resource_type: val})}>
                <SelectTrigger data-testid="resource-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Brief description..."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-ring min-h-[60px]" data-testid="resource-desc-input" />
            </div>
            <div className="space-y-1.5">
              <Label>File / Video URL *</Label>
              <Input value={form.file_url} onChange={(e) => setForm({...form, file_url: e.target.value})} placeholder="https://..." data-testid="resource-url-input" className="placeholder:text-slate-300" />
            </div>
            <div className="space-y-1.5">
              <Label>Thumbnail URL</Label>
              <Input value={form.thumbnail_url} onChange={(e) => setForm({...form, thumbnail_url: e.target.value})} placeholder="https://..." className="placeholder:text-slate-300" />
            </div>
            <div className="space-y-1.5">
              <Label>Linked Vertical</Label>
              <Select value={form.vertical_id || "none"} onValueChange={(val) => setForm({...form, vertical_id: val === "none" ? "" : val})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific vertical</SelectItem>
                  {verticals.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_published} onCheckedChange={(val) => setForm({...form, is_published: val})} />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0055aa]" data-testid="save-resource-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
